import { useState } from "react";
import { api, downloadUrl } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { toast } from "sonner";
import { FileJson, Trash2, ShieldCheck, LogOut } from "lucide-react";

export default function MemberData() {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const requestDeletion = async () => {
    setBusy(true);
    try {
      const res = await api("/membre/donnees/suppression", { method: "POST" });
      toast.success(res.message);
      setConfirmDelete(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const logoutAll = async () => {
    try {
      await api("/auth/logout-all", { method: "POST" });
      toast.success("Déconnecté de tous les appareils");
      window.location.href = "/connexion";
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <AppLayout>
      <div data-testid="member-data-page" className="max-w-3xl space-y-6 animate-fade-in">
        <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Mes données personnelles</h1>

        <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 space-y-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-cfhtb-success shrink-0 mt-0.5" />
            <div className="text-sm text-cfhtb-text leading-relaxed">
              <p className="font-medium text-cfhtb-heading">Vos droits (RGPD)</p>
              <p className="mt-1">
                Vous disposez d'un droit d'accès, de rectification, d'effacement et de portabilité
                de vos données. La publication dans l'annuaire et l'envoi de la newsletter reposent
                sur votre consentement, retirable à tout moment depuis votre profil.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-medium text-cfhtb-heading text-sm">Exporter mes données</p>
            <p className="text-xs text-cfhtb-text mt-1">
              Téléchargez l'ensemble de vos données (profil, fiche praticien, inscriptions) au format JSON.
            </p>
          </div>
          <a
            data-testid="rgpd-export-json-button"
            href={downloadUrl("/membre/donnees/export")}
            className="inline-flex items-center gap-2 bg-white text-cfhtb-primary border border-cfhtb-primary rounded-md px-4 py-2 text-sm font-medium hover:bg-cfhtb-bg transition-colors"
          >
            <FileJson className="h-4 w-4" />
            Télécharger (JSON)
          </a>
        </div>

        <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-medium text-cfhtb-heading text-sm">Déconnexion globale</p>
            <p className="text-xs text-cfhtb-text mt-1">
              Invalide toutes vos sessions actives sur tous vos appareils.
            </p>
          </div>
          <button
            data-testid="logout-all-button"
            onClick={logoutAll}
            className="inline-flex items-center gap-2 bg-white text-cfhtb-text border border-slate-300 rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-50 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Déconnecter tous mes appareils
          </button>
        </div>

        <div className="bg-white rounded-lg border border-cfhtb-error/30 shadow-sm p-6">
          <p className="font-medium text-cfhtb-heading text-sm">Suppression de mon compte</p>
          <p className="text-xs text-cfhtb-text mt-1 mb-4">
            Votre demande sera transmise à l'association, qui procédera à l'effacement de vos données
            dans les délais légaux (les obligations comptables peuvent imposer la conservation de certaines données).
          </p>
          {!confirmDelete ? (
            <button
              data-testid="rgpd-delete-account-button"
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-2 bg-cfhtb-error text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#a53024] transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Demander la suppression
            </button>
          ) : (
            <div className="rounded-md bg-cfhtb-error/5 border border-cfhtb-error/30 p-4 space-y-3" data-testid="rgpd-delete-confirm">
              <p className="text-sm text-cfhtb-heading font-medium">
                Confirmez-vous votre demande de suppression de compte ?
              </p>
              <div className="flex gap-3">
                <button
                  data-testid="rgpd-delete-confirm-button"
                  onClick={requestDeletion}
                  disabled={busy}
                  className="bg-cfhtb-error text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#a53024] transition-colors disabled:opacity-60"
                >
                  {busy ? "Envoi…" : "Oui, confirmer"}
                </button>
                <button
                  data-testid="rgpd-delete-cancel-button"
                  onClick={() => setConfirmDelete(false)}
                  className="bg-white text-cfhtb-text border border-slate-300 rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-50 transition-colors"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
